import { useState, useRef, useCallback, useEffect } from 'react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Hook for streaming clone analysis with live step-by-step progress.
 * Connects to GET /clone/projects/{id}/analyze/stream via SSE.
 *
 * Returns: { steps, isAnalyzing, isComplete, result, error, elapsedMs, startAnalysis }
 */
export default function useAnalysisStream(projectId) {
  const [steps, setSteps] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(0);

  const eventSourceRef = useRef(null);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  useEffect(() => {
    return () => {
      eventSourceRef.current?.close();
      clearInterval(timerRef.current);
    };
  }, []);

  const startAnalysis = useCallback((url) => {
    // Reset state
    setSteps([]);
    setIsAnalyzing(true);
    setIsComplete(false);
    setError(null);
    setResult(null);
    setElapsedMs(0);

    startTimeRef.current = Date.now();
    timerRef.current = setInterval(() => setElapsedMs(Date.now() - startTimeRef.current), 100);

    const token = localStorage.getItem('orbis_token');
    if (!token) {
      setError('Not authenticated');
      setIsAnalyzing(false);
      clearInterval(timerRef.current);
      return;
    }

    // Close any existing connection
    eventSourceRef.current?.close();

    const params = new URLSearchParams({ token, url });
    const es = new EventSource(`${API_URL}/clone/projects/${projectId}/analyze/stream?${params}`);
    eventSourceRef.current = es;

    es.addEventListener('step:start', (e) => {
      try {
        const data = JSON.parse(e.data);
        setSteps(prev => {
          const existing = prev.find(s => s.id === data.step_id);
          if (existing) return prev.map(s => s.id === data.step_id ? { ...s, status: 'running', label: data.label } : s);
          return [...prev, { id: data.step_id, label: data.label, status: 'running', index: data.index, total: data.total_steps }];
        });
      } catch {}
    });

    es.addEventListener('step:complete', (e) => {
      try {
        const data = JSON.parse(e.data);
        setSteps(prev => prev.map(s =>
          s.id === data.step_id
            ? { ...s, status: 'done', duration_ms: data.duration_ms, data: data.data }
            : s
        ));
      } catch {}
    });

    es.addEventListener('analysis:complete', (e) => {
      try {
        const data = JSON.parse(e.data);
        setResult(data);
        setIsComplete(true);
        setIsAnalyzing(false);
        clearInterval(timerRef.current);
        setElapsedMs(Date.now() - startTimeRef.current);
        es.close();
      } catch {}
    });

    es.addEventListener('step:error', (e) => {
      try {
        const data = JSON.parse(e.data);
        setError(data.error || 'Analysis failed');
        setIsAnalyzing(false);
        clearInterval(timerRef.current);
        es.close();
      } catch {}
    });

    es.onerror = () => {
      // SSE connection closed — if we have a result, it's a clean close
      if (!result) {
        setIsAnalyzing(false);
        clearInterval(timerRef.current);
        es.close();
      }
    };
  }, [projectId]);

  return { steps, isAnalyzing, isComplete, result, error, elapsedMs, startAnalysis };
}
